import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Car, Shield, Clock, Star, Instagram, ChevronRight, MessageCircle } from "lucide-react";
import LanguageSelector from "@/components/LanguageSelector";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReviewsDisplay = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews`);
      setReviews(response.data.slice(0, 6));
    } catch (error) {
      console.error("Error fetching reviews:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center text-gray-400">Loading reviews...</div>;
  }

  if (reviews.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-400">No reviews yet. Be the first to share your experience!</p>
      </div>
    );
  }

  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
      {reviews.map((review) => (
        <Card key={review.id} className="bg-gradient-to-br from-gray-900 to-black border-[#D4AF37]/20 shadow-2xl hover:shadow-[#D4AF37]/20 transition-all hover:scale-105 duration-300">
          <CardContent className="p-6">
            <div className="flex items-center gap-1 mb-3">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  className={`w-5 h-5 ${
                    star <= review.rating ? "fill-[#D4AF37] text-[#D4AF37]" : "text-gray-700"
                  }`}
                />
              ))}
            </div>
            <p className="text-gray-300 mb-4 leading-relaxed text-sm">"{review.comment}"</p>
            <div className="flex items-center justify-between border-t border-gray-800 pt-4">
              <div>
                <p className="font-semibold text-white">{review.user_name}</p>
                <p className="text-xs text-gray-500">
                  {new Date(review.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

const Landing = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-black">
      {/* Navbar */}
      <nav className="fixed top-0 w-full z-50 backdrop-blur-xl bg-black/80 border-b border-[#D4AF37]/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/")}>
              <img 
                src="/elite-cwb-logo.webp" 
                alt="Elite CWB Logo" 
                className="h-24 w-auto"
                style={{ filter: 'drop-shadow(0 0 10px rgba(212, 175, 55, 0.3))' }}
              />
            </div>
            <div className="flex items-center gap-4">
              <LanguageSelector />
              <a
                href="https://wa.me/4407301501871?text=Hello%2C%20I%27m%20interested%20in%20Elite%20CWB%20chauffeur%20service"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="whatsapp-nav-link"
                className="text-green-500 hover:text-green-400 transition-colors"
              >
                <MessageCircle className="w-6 h-6" />
              </a>
              <a
                href="https://www.instagram.com/elitecwbchauffeur?igsh=NmZ4MWkwNzVuZDIw"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="instagram-nav-link"
                className="text-[#D4AF37] hover:text-[#F7E7CE] transition-colors"
              >
                <Instagram className="w-6 h-6" />
              </a>
              {user ? (
                <>
                  <Button 
                    data-testid="dashboard-nav-btn"
                    variant="ghost" 
                    onClick={() => navigate(user.role === "admin" ? "/admin" : "/dashboard")}
                    className="text-gray-300 hover:text-white hover:bg-white/10"
                  >
                    {t('nav.dashboard')}
                  </Button>
                  <Button 
                    data-testid="logout-nav-btn"
                    variant="outline" 
                    onClick={onLogout}
                    className="border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10"
                  >
                    {t('nav.logout')}
                  </Button>
                </>
              ) : (
                <>
                  <Button 
                    data-testid="login-nav-btn"
                    variant="ghost" 
                    onClick={() => navigate("/login")}
                    className="text-gray-300 hover:text-white hover:bg-white/10"
                  >
                    {t('nav.login')}
                  </Button>
                  <Button 
                    data-testid="register-nav-btn"
                    onClick={() => navigate("/register")}
                    className="bg-gradient-to-r from-[#D4AF37] to-[#B8860B] text-black font-semibold hover:from-[#F7E7CE] hover:to-[#D4AF37] shadow-lg shadow-[#D4AF37]/30 border border-[#D4AF37]/20"
                  >
                    {t('nav.register')}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 relative overflow-hidden">
        {/* Background Video */}
        <div className="absolute inset-0">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
          >
            <source src="https://customer-assets.emergentagent.com/job_elite-chauffeur-4/artifacts/wfizhyw7__88ae1252-3ef6-4b54-8c0c-936ae6a47dcf.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black"></div>
        </div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block mb-6">
                <Badge className="bg-gradient-to-r from-[#D4AF37] to-[#D4AF37] text-black border-0 px-4 py-2 text-sm font-semibold shadow-lg shadow-[#D4AF37]/30">
                  {t('landing.badge')}
                </Badge>
              </div>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
                {t('landing.title')}
                <span className="block bg-gradient-to-r from-[#D4AF37] via-[#F7E7CE] to-[#D4AF37] bg-clip-text text-transparent gold-glow">{t('landing.titleHighlight')}</span>
              </h1>
              <p className="text-lg text-gray-400 mb-8 leading-relaxed">
                {t('landing.subtitle')}
              </p>
              <div className="flex flex-wrap gap-4">
                <a 
                  href="https://wa.me/4407301501871?text=Hello%2C%20I%27m%20interested%20in%20booking%20a%20chauffeur%20service%20with%20Elite%20CWB"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="whatsapp-hero-btn"
                >
                  <Button 
                    size="lg" 
                    className="bg-gradient-to-r from-[#D4AF37] to-[#D4AF37] text-black font-bold hover:from-[#D4AF37] hover:to-[#F7E7CE] text-lg px-8 py-6 shadow-2xl shadow-[#D4AF37]/50 group"
                  >
                    <MessageCircle className="mr-2 group-hover:scale-110 transition-transform" />
                    {t('landing.whatsappContact')}
                  </Button>
                </a>
                <Button 
                  data-testid="book-now-hero-btn"
                  size="lg" 
                  onClick={() => navigate("/booking")}
                  className="bg-gradient-to-r from-[#D4AF37] to-[#D4AF37] text-black font-bold hover:from-[#D4AF37] hover:to-[#F7E7CE] text-lg px-8 py-6 shadow-2xl shadow-[#D4AF37]/50 group"
                >
                  {t('landing.bookOnline')}
                  <ChevronRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-square rounded-2xl overflow-hidden shadow-2xl shadow-[#D4AF37]/20 border border-[#D4AF37]/20">
                <img 
                  src="https://images.unsplash.com/photo-1698840059740-ba83e510733b?w=800&q=80" 
                  alt="Mercedes EQV300 - Elite Chauffeur Service" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-gradient-to-br from-gray-900 to-black p-6 rounded-xl shadow-2xl border border-[#D4AF37]/30 backdrop-blur-xl">
                <div className="flex items-center gap-3">
                  <div className="bg-[#D4AF37]/20 p-3 rounded-full">
                    <Star className="w-6 h-6 text-[#D4AF37] fill-[#D4AF37]" />
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-white">5.0</div>
                    <div className="text-sm text-gray-400">Perfect Rating</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mercedes EQV300 Showcase */}
      <section className="py-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-black via-gray-900 to-black"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1698840059740-ba83e510733b?w=1920&q=80')"
          }}
        ></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="max-w-2xl">
            <Badge className="mb-6 bg-gradient-to-r from-green-600 to-emerald-500 text-white border-0 px-4 py-2">{t('landing.eqvBadge')}</Badge>
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">{t('landing.eqvTitle')}</h2>
            <p className="text-xl text-gray-300 mb-6 leading-relaxed">
              {t('landing.eqvDesc')}
            </p>
            <div className="grid grid-cols-2 gap-4 mb-8">
              <div className="p-4 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10">
                <div className="text-[#D4AF37] font-bold text-2xl mb-1">{t('landing.eqvElectric')}</div>
                <div className="text-gray-400 text-sm">{t('landing.eqvElectricLabel')}</div>
              </div>
              <div className="p-4 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10">
                <div className="text-[#D4AF37] font-bold text-2xl mb-1">{t('landing.eqvPassengers')}</div>
                <div className="text-gray-400 text-sm">{t('landing.eqvPassengersLabel')}</div>
              </div>
              <div className="p-4 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10">
                <div className="text-[#D4AF37] font-bold text-2xl mb-1">{t('landing.eqvInterior')}</div>
                <div className="text-gray-400 text-sm">{t('landing.eqvInteriorLabel')}</div>
              </div>
              <div className="p-4 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10">
                <div className="text-[#D4AF37] font-bold text-2xl mb-1">{t('landing.eqvSilent')}</div>
                <div className="text-gray-400 text-sm">{t('landing.eqvSilentLabel')}</div>
              </div>
            </div>
            <Button 
              data-testid="book-eqv300-btn"
              onClick={() => navigate("/booking")}
              size="lg"
              className="bg-gradient-to-r from-[#D4AF37] to-[#D4AF37] text-black font-bold hover:from-[#D4AF37] hover:to-[#F7E7CE] shadow-xl shadow-[#D4AF37]/30"
            >
              {t('landing.bookEQV')}
            </Button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">{t('landing.whyChooseTitle')}</h2>
            <p className="text-lg text-gray-400">{t('landing.whyChooseSubtitle')}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="p-8 rounded-2xl bg-gradient-to-br from-gray-900 to-black border border-[#D4AF37]/20 hover:border-[#D4AF37]/50 transition-all hover:scale-105 duration-300">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#D4AF37] flex items-center justify-center mb-6">
                <Car className="w-8 h-8 text-black" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">{t('landing.fleetTitle')}</h3>
              <p className="text-gray-400 leading-relaxed">
                {t('landing.fleetDesc')}
              </p>
            </div>
            <div className="p-8 rounded-2xl bg-gradient-to-br from-gray-900 to-black border border-[#D4AF37]/20 hover:border-[#D4AF37]/50 transition-all hover:scale-105 duration-300">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#D4AF37] flex items-center justify-center mb-6">
                <Shield className="w-8 h-8 text-black" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">{t('landing.driversTitle')}</h3>
              <p className="text-gray-400 leading-relaxed">
                {t('landing.driversDesc')}
              </p>
            </div>
            <div className="p-8 rounded-2xl bg-gradient-to-br from-gray-900 to-black border border-[#D4AF37]/20 hover:border-[#D4AF37]/50 transition-all hover:scale-105 duration-300">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#D4AF37] flex items-center justify-center mb-6">
                <Clock className="w-8 h-8 text-black" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">{t('landing.availabilityTitle')}</h3>
              <p className="text-gray-400 leading-relaxed">
                {t('landing.availabilityDesc')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Fleet Gallery Section */}
      <section className="py-20 px-4 bg-black">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">{t('landing.ourFleetTitle')}</h2>
            <p className="text-lg text-gray-400">{t('landing.ourFleetSubtitle')}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="group relative overflow-hidden rounded-2xl border border-[#D4AF37]/20 hover:border-[#D4AF37]/50 transition-all">
              <div className="aspect-[4/3] overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1607332623489-e8ddd788072d?w=800&q=80" 
                  alt="Mercedes S-Class" 
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/90 to-transparent p-6">
                <h3 className="text-2xl font-bold text-white mb-2">{t('landing.sClass')}</h3>
                <p className="text-gray-300 text-sm">{t('landing.sClassDesc')}</p>
              </div>
            </div>
            <div className="group relative overflow-hidden rounded-2xl border border-[#D4AF37]/20 hover:border-[#D4AF37]/50 transition-all">
              <div className="aspect-[4/3] overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1565340380388-6ecb7426fc63?w=800&q=80" 
                  alt="Mercedes V-Class" 
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/90 to-transparent p-6">
                <h3 className="text-2xl font-bold text-white mb-2">{t('landing.vClass')}</h3>
                <p className="text-gray-300 text-sm">{t('landing.vClassDesc')}</p>
              </div>
            </div>
            <div className="group relative overflow-hidden rounded-2xl border border-[#D4AF37]/20 hover:border-[#D4AF37]/50 transition-all">
              <div className="aspect-[4/3] overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1664612414212-e6aef1ff8441?w=800&q=80" 
                  alt="Mercedes E-Class" 
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/90 to-transparent p-6">
                <h3 className="text-2xl font-bold text-white mb-2">{t('landing.eClass')}</h3>
                <p className="text-gray-300 text-sm">{t('landing.eClassDesc')}</p>
              </div>
            </div>
          </div>
          <div className="mt-12 text-center">
            <Button 
              data-testid="view-all-fleet-btn"
              onClick={() => navigate("/booking")}
              size="lg"
              className="bg-gradient-to-r from-[#D4AF37] to-[#D4AF37] text-black font-bold hover:from-[#D4AF37] hover:to-[#F7E7CE] shadow-xl shadow-[#D4AF37]/30"
            >
              {t('landing.viewAllFleet')}
            </Button>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-gray-900 via-black to-gray-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">{t('landing.servicesTitle')}</h2>
            <p className="text-lg text-gray-400">{t('landing.servicesSubtitle')}</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="p-8 rounded-2xl bg-gradient-to-br from-gray-900 to-black border border-[#D4AF37]/20 hover:border-[#D4AF37]/50 transition-all">
              <h3 className="text-2xl font-bold text-white mb-3">{t('landing.airportTitle')}</h3>
              <p className="text-gray-400 mb-4 leading-relaxed">
                {t('landing.airportDesc')}
              </p>
              <ul className="space-y-2 text-gray-400">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full"></div>
                  {t('landing.airportFeature1')}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full"></div>
                  {t('landing.airportFeature2')}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full"></div>
                  {t('landing.airportFeature3')}
                </li>
              </ul>
            </div>
            <div className="p-8 rounded-2xl bg-gradient-to-br from-gray-900 to-black border border-[#D4AF37]/20 hover:border-[#D4AF37]/50 transition-all">
              <h3 className="text-2xl font-bold text-white mb-3">{t('landing.asDirectedTitle')}</h3>
              <p className="text-gray-400 mb-4 leading-relaxed">
                {t('landing.asDirectedDesc')}
              </p>
              <ul className="space-y-2 text-gray-400">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full"></div>
                  {t('landing.asDirectedFeature1')}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full"></div>
                  {t('landing.asDirectedFeature2')}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full"></div>
                  {t('landing.asDirectedFeature3')}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Tours Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">{t('landing.toursTitle')}</h2>
            <p className="text-lg text-gray-400">{t('landing.toursSubtitle')}</p>
          </div>
          
          {/* City Tours */}
          <div className="mb-16">
            <h3 className="text-3xl font-bold text-white mb-8 flex items-center gap-3">
              <div className="w-2 h-8 bg-gradient-to-b from-[#D4AF37] to-[#D4AF37] rounded-full"></div>
              {t('landing.cityToursTitle')}
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="group p-6 rounded-2xl bg-gradient-to-br from-gray-900 to-black border border-[#D4AF37]/20 hover:border-[#D4AF37]/50 transition-all hover:scale-105 duration-300">
                <div className="text-[#D4AF37] text-sm font-semibold mb-2">4 HOURS</div>
                <h4 className="text-xl font-bold text-white mb-3">London Highlights</h4>
                <p className="text-gray-400 text-sm mb-4">Buckingham Palace, Big Ben, Tower Bridge, Westminster Abbey</p>
                <div className="text-sm text-[#D4AF37]">Get a quote</div>
              </div>
              <div className="group p-6 rounded-2xl bg-gradient-to-br from-gray-900 to-black border border-[#D4AF37]/20 hover:border-[#D4AF37]/50 transition-all hover:scale-105 duration-300">
                <div className="text-[#D4AF37] text-sm font-semibold mb-2">3 HOURS</div>
                <h4 className="text-xl font-bold text-white mb-3">Royal London</h4>
                <p className="text-gray-400 text-sm mb-4">Royal palaces, changing of the guard, Kensington Palace</p>
                <div className="text-sm text-[#D4AF37]">Get a quote</div>
              </div>
              <div className="group p-6 rounded-2xl bg-gradient-to-br from-gray-900 to-black border border-[#D4AF37]/20 hover:border-[#D4AF37]/50 transition-all hover:scale-105 duration-300">
                <div className="text-[#D4AF37] text-sm font-semibold mb-2">4 HOURS</div>
                <h4 className="text-xl font-bold text-white mb-3">Historic London</h4>
                <p className="text-gray-400 text-sm mb-4">Tower of London, St Paul's, British Museum, Covent Garden</p>
                <div className="text-sm text-[#D4AF37]">Get a quote</div>
              </div>
            </div>
          </div>

          {/* UK Tours */}
          <div>
            <h3 className="text-3xl font-bold text-white mb-8 flex items-center gap-3">
              <div className="w-2 h-8 bg-gradient-to-b from-[#D4AF37] to-[#D4AF37] rounded-full"></div>
              {t('landing.ukToursTitle')}
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="group relative overflow-hidden rounded-2xl border border-[#D4AF37]/20 hover:border-[#D4AF37]/50 transition-all hover:scale-105 duration-300">
                <div className="aspect-[4/3] overflow-hidden">
                  <img 
                    src="https://images.unsplash.com/photo-1757581559631-dbb395c7a059?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxDb3Rzd29sZHMlMjB2aWxsYWdlc3xlbnwwfHx8fDE3NjE3Mzc4MTh8MA&ixlib=rb-4.1.0&q=85" 
                    alt="Cotswolds" 
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/90 to-transparent p-6">
                  <div className="text-[#D4AF37] text-xs font-semibold mb-1">FULL DAY</div>
                  <h4 className="text-xl font-bold text-white mb-1">Cotswolds</h4>
                  <p className="text-gray-300 text-sm mb-2">Rolling hills & charming villages</p>
                  <div className="text-sm text-[#D4AF37]">Get a quote</div>
                </div>
              </div>
              <div className="group relative overflow-hidden rounded-2xl border border-[#D4AF37]/20 hover:border-[#D4AF37]/50 transition-all hover:scale-105 duration-300">
                <div className="aspect-[4/3] overflow-hidden">
                  <img 
                    src="https://images.unsplash.com/photo-1612563958093-2c3bcfbd8760?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxPeGZvcmQlMjBVbml2ZXJzaXR5fGVufDB8fHx8MTc2MTczNzgyMnww&ixlib=rb-4.1.0&q=85" 
                    alt="Oxford" 
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/90 to-transparent p-6">
                  <div className="text-[#D4AF37] text-xs font-semibold mb-1">FULL DAY</div>
                  <h4 className="text-xl font-bold text-white mb-1">Oxford & Cotswolds</h4>
                  <p className="text-gray-300 text-sm mb-2">Historic university & countryside</p>
                  <div className="text-sm text-[#D4AF37]">Get a quote</div>
                </div>
              </div>
              <div className="group relative overflow-hidden rounded-2xl border border-[#D4AF37]/20 hover:border-[#D4AF37]/50 transition-all hover:scale-105 duration-300">
                <div className="aspect-[4/3] overflow-hidden">
                  <img 
                    src="https://images.unsplash.com/photo-1599833975787-5c143f373c30?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxTdG9uZWhlbmdlfGVufDB8fHx8MTc2MTczNzgyN3ww&ixlib=rb-4.1.0&q=85" 
                    alt="Stonehenge" 
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/90 to-transparent p-6">
                  <div className="text-[#D4AF37] text-xs font-semibold mb-1">FULL DAY</div>
                  <h4 className="text-xl font-bold text-white mb-1">Stonehenge & Bath</h4>
                  <p className="text-gray-300 text-sm mb-2">Ancient monuments & Roman baths</p>
                  <div className="text-sm text-[#D4AF37]">Get a quote</div>
                </div>
              </div>
              <div className="group relative overflow-hidden rounded-2xl border border-[#D4AF37]/20 hover:border-[#D4AF37]/50 transition-all hover:scale-105 duration-300">
                <div className="aspect-[4/3] overflow-hidden">
                  <img 
                    src="https://images.unsplash.com/photo-1630188397757-15ddada2772b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxXaW5kc29yJTIwQ2FzdGxlfGVufDB8fHx8MTc2MTczNzgzMnww&ixlib=rb-4.1.0&q=85" 
                    alt="Windsor Castle" 
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/90 to-transparent p-6">
                  <div className="text-[#D4AF37] text-xs font-semibold mb-1">HALF DAY</div>
                  <h4 className="text-xl font-bold text-white mb-1">Windsor & Eton</h4>
                  <p className="text-gray-300 text-sm mb-2">Royal Windsor Castle & Eton College</p>
                  <div className="text-sm text-[#D4AF37]">Get a quote</div>
                </div>
              </div>
            </div>
            
            <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="group relative overflow-hidden rounded-2xl border border-[#D4AF37]/20 hover:border-[#D4AF37]/50 transition-all hover:scale-105 duration-300">
                <div className="aspect-[4/3] overflow-hidden">
                  <img 
                    src="https://images.unsplash.com/photo-1654685904397-83453f185259?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxDYW1icmlkZ2UlMjBjb2xsZWdlc3xlbnwwfHx8fDE3NjE3Mzc4Mzd8MA&ixlib=rb-4.1.0&q=85" 
                    alt="Cambridge" 
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/90 to-transparent p-6">
                  <div className="text-[#D4AF37] text-xs font-semibold mb-1">FULL DAY</div>
                  <h4 className="text-xl font-bold text-white mb-1">Cambridge</h4>
                  <p className="text-gray-300 text-sm mb-2">Historic colleges & punting on River Cam</p>
                  <div className="text-sm text-[#D4AF37]">Get a quote</div>
                </div>
              </div>
              <div className="group relative overflow-hidden rounded-2xl border border-[#D4AF37]/20 hover:border-[#D4AF37]/50 transition-all hover:scale-105 duration-300">
                <div className="aspect-[4/3] overflow-hidden">
                  <img 
                    src="https://images.unsplash.com/photo-1667018030671-ab416cb31331?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxCcmlnaHRvbiUyMFNldmVuJTIwU2lzdGVycyUyMGNsaWZmc3xlbnwwfHx8fDE3NjE3Mzc4NDJ8MA&ixlib=rb-4.1.0&q=85" 
                    alt="Brighton Seven Sisters" 
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/90 to-transparent p-6">
                  <div className="text-[#D4AF37] text-xs font-semibold mb-1">FULL DAY</div>
                  <h4 className="text-xl font-bold text-white mb-1">Brighton & Seven Sisters</h4>
                  <p className="text-gray-300 text-sm mb-2">Seaside charm & stunning white cliffs</p>
                  <div className="text-sm text-[#D4AF37]">Get a quote</div>
                </div>
              </div>
              <div className="group relative overflow-hidden rounded-2xl border border-[#D4AF37]/20 hover:border-[#D4AF37]/50 transition-all hover:scale-105 duration-300">
                <div className="aspect-[4/3] overflow-hidden">
                  <img 
                    src="https://images.unsplash.com/photo-1503623190251-09e94b303ef2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxDYW50ZXJidXJ5JTIwQ2F0aGVkcmFsfGVufDB8fHx8MTc2MTczNzg1M3ww&ixlib=rb-4.1.0&q=85" 
                    alt="Canterbury Cathedral" 
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/90 to-transparent p-6">
                  <div className="text-[#D4AF37] text-xs font-semibold mb-1">FULL DAY</div>
                  <h4 className="text-xl font-bold text-white mb-1">Canterbury & Dover</h4>
                  <p className="text-gray-300 text-sm mb-2">Medieval cathedral & white cliffs</p>
                  <div className="text-sm text-[#D4AF37]">Get a quote</div>
                </div>
              </div>
              <div className="group relative overflow-hidden rounded-2xl border border-[#D4AF37]/20 hover:border-[#D4AF37]/50 transition-all hover:scale-105 duration-300">
                <div className="aspect-[4/3] overflow-hidden">
                  <img 
                    src="https://images.unsplash.com/photo-1568587166637-7ad1aef43288?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxTdHJhdGZvcmQtdXBvbi1Bdm9uJTIwU2hha2VzcGVhcmV8ZW58MHx8fHwxNzYxNzM3ODU4fDA&ixlib=rb-4.1.0&q=85" 
                    alt="Stratford-upon-Avon" 
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/90 to-transparent p-6">
                  <div className="text-[#D4AF37] text-xs font-semibold mb-1">FULL DAY</div>
                  <h4 className="text-xl font-bold text-white mb-1">Stratford-upon-Avon</h4>
                  <p className="text-gray-300 text-sm mb-2">Shakespeare's birthplace & heritage</p>
                  <div className="text-sm text-[#D4AF37]">Get a quote</div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-12 text-center">
            <div className="flex flex-wrap gap-4 justify-center">
              <a 
                href="https://wa.me/4407301501871?text=Hello%2C%20I%27d%20like%20to%20get%20a%20quote%20for%20a%20tour"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button 
                  size="lg"
                  className="bg-gradient-to-r from-green-600 to-green-500 text-white font-bold hover:from-green-500 hover:to-green-400 shadow-xl shadow-green-500/30"
                >
                  <MessageCircle className="mr-2" />
                  Get Quote on WhatsApp
                </Button>
              </a>
              <Button 
                onClick={() => navigate("/booking")}
                size="lg"
                className="bg-gradient-to-r from-[#D4AF37] to-[#D4AF37] text-black font-bold hover:from-[#D4AF37] hover:to-[#F7E7CE] shadow-xl shadow-[#D4AF37]/30"
              >
                Book Online
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="py-20 px-4 bg-black">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">{t('landing.reviewsTitle')}</h2>
            <p className="text-lg text-gray-400">{t('landing.reviewsSubtitle')}</p>
          </div>
          <ReviewsDisplay />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/20 via-black to-[#D4AF37]/20"></div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">{t('landing.ctaTitle')}</h2>
          <p className="text-lg text-gray-300 mb-8">
            {t('landing.ctaSubtitle')}
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a 
              href="https://wa.me/4407301501871?text=Hello%2C%20I%27d%20like%20to%20get%20a%20quote%20for%20a%20chauffeur%20service"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="whatsapp-cta-btn"
            >
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-green-600 to-green-500 text-white font-bold hover:from-green-500 hover:to-green-400 text-lg px-12 py-6 shadow-2xl shadow-green-500/50"
              >
                <MessageCircle className="mr-2" />
                WhatsApp Us Now
              </Button>
            </a>
            <Button 
              data-testid="book-now-cta-btn"
              size="lg" 
              onClick={() => navigate("/booking")}
              className="bg-gradient-to-r from-[#D4AF37] to-[#D4AF37] text-black font-bold hover:from-[#D4AF37] hover:to-[#F7E7CE] text-lg px-12 py-6 shadow-2xl shadow-[#D4AF37]/50"
            >
              Book Online
            </Button>
          </div>
        </div>
      </section>

      {/* Floating WhatsApp Button */}
      <a
        href="https://wa.me/4407301501871?text=Hello%2C%20I%27m%20interested%20in%20booking%20a%20chauffeur%20service%20with%20Elite%20CWB"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="floating-whatsapp-btn"
        className="fixed bottom-6 right-6 z-50 bg-gradient-to-r from-green-600 to-green-500 text-white p-4 rounded-full shadow-2xl shadow-green-500/50 hover:scale-110 transition-transform duration-300 group"
      >
        <MessageCircle className="w-8 h-8 group-hover:rotate-12 transition-transform" />
        <span className="absolute -top-1 -right-1 flex h-3 w-3">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
        </span>
      </a>

      {/* Footer */}
      <footer className="bg-gradient-to-b from-black to-gray-900 text-white py-12 px-4 border-t border-[#D4AF37]/20">
        <div className="max-w-7xl mx-auto text-center">
          <img 
            src="/elite-cwb-logo.webp" 
            alt="Elite CWB Logo" 
            className="h-28 w-auto mx-auto mb-6"
            style={{ filter: 'drop-shadow(0 0 15px rgba(212, 175, 55, 0.4))' }}
          />
          <p className="text-gray-400 mb-4">{t('landing.footerText')}</p>
          
          <div className="flex justify-center gap-6 mb-6 text-sm">
            <button 
              onClick={() => navigate("/cancellation-policy")}
              className="text-gray-500 hover:text-[#D4AF37] transition-colors"
            >
              {t('landing.cancellationPolicy')}
            </button>
          </div>
          
          <div className="flex justify-center gap-6 mb-6">
            <a
              href="https://www.instagram.com/elitecwbchauffeur?igsh=NmZ4MWkwNzVuZDIw"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="instagram-footer-link"
              className="text-gray-400 hover:text-[#D4AF37] transition-colors group"
            >
              <div className="flex items-center gap-2">
                <div className="p-3 rounded-full border border-[#D4AF37]/30 group-hover:border-[#D4AF37] group-hover:bg-[#D4AF37]/10 transition-all">
                  <Instagram className="w-6 h-6" />
                </div>
                <span className="text-sm">{t('landing.followInstagram')}</span>
              </div>
            </a>
          </div>
          
          <p className="text-gray-600 text-sm">{t('landing.copyright')}</p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;